import { useCallback, useState } from "react";
import { Camera, Film, Images, X } from "lucide-react";
import { useIsAppMode } from "@/app-bridge/hooks/useIsAppMode";

type ImageRequest = {
  /** Flera bilder åt gången? */
  multiple?: boolean;
  maxImages?: number;
  /** Anropas med bilderna från telefonen. */
  onFiles: (files: File[]) => void;
  /** Öppnar webbens vanliga filväljare (fallback). */
  openWebPicker: () => void;
};

type VideoRequest = {
  onFile: (file: File) => void;
  openWebPicker: () => void;
};

type Pending = ({ kind: "image" } & ImageRequest) | ({ kind: "video" } & VideoRequest) | null;

/**
 * Gemensam väljare för bilder och film. I appen visas telefonens egna
 * gränssnitt (kamera eller bildbibliotek); i webbläsaren används den vanliga
 * filväljaren precis som förut. Allt faller tillbaka till webben om ett
 * native-anrop saknas, avbryts eller misslyckas.
 */
export function useMediaPicker() {
  const appMode = useIsAppMode();
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);

  const pickImages = useCallback(
    (req: ImageRequest) => {
      if (!appMode) {
        req.openWebPicker();
        return;
      }
      setPending({ kind: "image", ...req });
    },
    [appMode],
  );

  const pickVideo = useCallback(
    (req: VideoRequest) => {
      if (!appMode) {
        req.openWebPicker();
        return;
      }
      setPending({ kind: "video", ...req });
    },
    [appMode],
  );

  const close = () => {
    setBusy(false);
    setPending(null);
  };

  const runImage = async (source: "camera" | "library") => {
    if (!pending || pending.kind !== "image") return;
    const req = pending;
    setBusy(true);
    const m = await import("@/app-bridge/core/wrapper");
    try {
      if (source === "camera") {
        const photo = await m.takeNativePhoto();
        if (photo) {
          req.onFiles([photo]);
          close();
          return;
        }
      } else {
        const files = await m.pickNativePhotos({
          multiple: req.multiple ?? false,
          ...(req.maxImages ? { maxImages: req.maxImages } : {}),
        });
        if (files?.length) {
          req.onFiles(files);
          close();
          return;
        }
      }
    } catch {
      /* faller tillbaka nedan */
    }
    close();
    req.openWebPicker();
  };

  const runVideo = async (source: "camera" | "library") => {
    if (!pending || pending.kind !== "video") return;
    const req = pending;
    if (source === "library") {
      close();
      req.openWebPicker();
      return;
    }
    setBusy(true);
    const m = await import("@/app-bridge/core/wrapper");
    const clip = await m.recordNativeVideo();
    close();
    if (clip) req.onFile(clip);
    else req.openWebPicker();
  };

  const sheet = pending ? (
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center bg-foreground/40 backdrop-blur-sm"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label={pending.kind === "video" ? "Välj film" : "Välj bild"}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-2xl border border-border bg-card p-3 pb-[calc(var(--safe-bottom,0px)+0.75rem)] shadow-2xl"
      >
        <div className="mb-2 flex items-center justify-between px-2 pt-1">
          <p className="font-heading text-sm font-semibold">
            {pending.kind === "video" ? "Lägg till film" : "Lägg till bild"}
          </p>
          <button
            type="button"
            onClick={close}
            aria-label="Stäng"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors active:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="px-2 pb-3 text-xs leading-relaxed text-muted-foreground">
          {pending.kind === "video"
            ? "Filmen används bara i din annons. Vi spelar aldrig in i bakgrunden."
            : "Bilderna används bara i din annons och för att AI:n ska kunna skriva texten. Vi läser inget annat i bildbiblioteket."}
        </p>
        <div className="overflow-hidden rounded-xl border border-border">
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              void (pending.kind === "video" ? runVideo("camera") : runImage("camera"))
            }
            className="flex w-full items-center gap-3 border-b border-border/60 px-4 py-3.5 text-left text-[15px] font-medium transition-colors active:bg-muted disabled:opacity-60"
          >
            {pending.kind === "video" ? (
              <Film className="h-5 w-5 text-muted-foreground" />
            ) : (
              <Camera className="h-5 w-5 text-muted-foreground" />
            )}
            {pending.kind === "video" ? "Spela in film" : "Ta foto"}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              void (pending.kind === "video" ? runVideo("library") : runImage("library"))
            }
            className="flex w-full items-center gap-3 px-4 py-3.5 text-left text-[15px] font-medium transition-colors active:bg-muted disabled:opacity-60"
          >
            <Images className="h-5 w-5 text-muted-foreground" />
            {pending.kind === "video" ? "Välj film från enheten" : "Välj ur bildbiblioteket"}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { pickImages, pickVideo, sheet, appMode };
}
